import type { AntSimWorld, PlayerControl, AntState } from '../sim/contracts';

type HudWorld = AntSimWorld & PlayerControl;

/** Tolerate `hunger` not yet existing on AntState while the other agent lands it. */
type MaybeHunger = Partial<Pick<AntState, 'hunger'>>;

/**
 * Bottom-left status panel for the yellow player ant: HP and fullness bars.
 * Reads live state each frame via update(); no internal tick state.
 */
export class PlayerHUD {
  readonly dom: HTMLElement;
  private deathBanner: HTMLDivElement;

  private hpFill: HTMLDivElement;
  private hpText: HTMLSpanElement;
  private hpLabel: HTMLSpanElement;
  private foodFill: HTMLDivElement;
  private foodText: HTMLSpanElement;

  constructor(private world: HudWorld) {
    // Centered death banner — big, obvious, auto-fades when player respawns.
    this.deathBanner = document.createElement('div');
    this.deathBanner.style.cssText = [
      'position:fixed',
      'top:50%',
      'left:50%',
      'transform:translate(-50%,-50%)',
      'background:rgba(140,20,20,0.88)',
      'color:#fff',
      'border:1px solid rgba(255,255,255,0.2)',
      'border-radius:10px',
      'padding:18px 32px',
      'font:600 18px/1.3 system-ui, sans-serif',
      'letter-spacing:0.04em',
      'text-transform:uppercase',
      'text-align:center',
      'box-shadow:0 8px 28px rgba(0,0,0,0.6)',
      'pointer-events:none',
      'z-index:9',
      'opacity:0',
      'transition:opacity 180ms ease',
    ].join(';');
    this.deathBanner.innerHTML = 'You died<br><span style="font-size:12px;opacity:0.85;font-weight:400;text-transform:none;letter-spacing:0;">Respawning at your colony…</span>';
    document.body.appendChild(this.deathBanner);
    const root = document.createElement('div');
    root.style.cssText = [
      'position:fixed',
      'bottom:8px',
      'left:8px',
      'width:220px',
      'background:rgba(15,20,25,0.78)',
      'color:#eee',
      'border-radius:8px',
      'padding:8px 10px',
      'font:11px/1.3 system-ui, sans-serif',
      'pointer-events:none',
      'z-index:5',
      'box-shadow:0 2px 8px rgba(0,0,0,0.4)',
      'display:flex',
      'flex-direction:column',
      'gap:4px',
    ].join(';');

    const title = document.createElement('div');
    title.textContent = 'YELLOW ANT';
    title.style.cssText = [
      'font-size:10px',
      'text-transform:uppercase',
      'letter-spacing:0.12em',
      'opacity:0.55',
    ].join(';');

    const hpRow = this.makeBarRow('HP');
    this.hpLabel = hpRow.label;
    this.hpFill = hpRow.fill;
    this.hpText = hpRow.text;

    const foodRow = this.makeBarRow('FOOD');
    this.foodFill = foodRow.fill;
    this.foodText = foodRow.text;

    root.appendChild(title);
    root.appendChild(hpRow.row);
    root.appendChild(foodRow.row);
    document.body.appendChild(root);

    this.dom = root;
    this.renderBars(1, 1);
  }

  private makeBarRow(labelText: string): {
    row: HTMLDivElement;
    label: HTMLSpanElement;
    fill: HTMLDivElement;
    text: HTMLSpanElement;
  } {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:6px';

    const label = document.createElement('span');
    label.textContent = labelText;
    label.style.cssText = 'flex:0 0 auto;font-size:10px;opacity:0.75;min-width:28px';

    const track = document.createElement('div');
    track.style.cssText = [
      'flex:1 1 auto',
      'height:16px',
      'border:1px solid rgba(255,255,255,0.25)',
      'border-radius:2px',
      'background:rgba(0,0,0,0.35)',
      'overflow:hidden',
      'position:relative',
    ].join(';');

    const fill = document.createElement('div');
    fill.style.cssText = [
      'height:100%',
      'width:0%',
      'border-radius:1px',
      'transition:width 120ms linear, background-color 120ms linear',
      'background:#4caf50',
    ].join(';');
    track.appendChild(fill);

    const text = document.createElement('span');
    text.textContent = '0%';
    text.style.cssText = 'flex:0 0 auto;font-size:10px;opacity:0.85;min-width:32px;text-align:right';

    row.appendChild(label);
    row.appendChild(track);
    row.appendChild(text);
    return { row, label, fill, text };
  }

  update(): void {
    const p = this.world.ants[this.world.playerId];
    if (!p || !p.alive) {
      this.hpLabel.textContent = 'DEAD';
      this.renderBars(0, 0);
      this.hpText.textContent = 'respawning\u2026';
      this.deathBanner.style.opacity = '1';
      return;
    }
    this.deathBanner.style.opacity = '0';
    this.hpLabel.textContent = 'HP';
    const hunger = (p as AntState & MaybeHunger).hunger ?? 0;
    const hp = clamp01(p.hp);
    const fullness = clamp01(1 - hunger);
    this.renderBars(hp, fullness);
  }

  private renderBars(hp: number, fullness: number): void {
    this.hpFill.style.width = `${Math.round(hp * 100)}%`;
    this.hpFill.style.background = hpColor(hp);
    // Only overwrite the HP text in the alive path; dead path sets its own.
    if (this.hpLabel.textContent === 'HP') {
      this.hpText.textContent = `${Math.round(hp * 100)}%`;
    }

    this.foodFill.style.width = `${Math.round(fullness * 100)}%`;
    this.foodFill.style.background = foodColor(fullness);
    this.foodText.textContent = `${Math.round(fullness * 100)}%`;
  }
}

function clamp01(x: number): number {
  if (!Number.isFinite(x)) return 0;
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

/** Red (<30%) -> yellow (~50%) -> green (full). Smooth RGB lerp between stops. */
function hpColor(t: number): string {
  const red: [number, number, number] = [0xd1, 0x3a, 0x2e];
  const yellow: [number, number, number] = [0xe6, 0xc2, 0x3a];
  const green: [number, number, number] = [0x4c, 0xc2, 0x4c];
  if (t <= 0.3) {
    // Below 30% stay firmly red; fade towards yellow at the top of that band.
    const k = t / 0.3;
    return rgbCss(lerp3(red, red, k));
  }
  if (t < 0.5) {
    const k = (t - 0.3) / 0.2;
    return rgbCss(lerp3(red, yellow, k));
  }
  const k = (t - 0.5) / 0.5;
  return rgbCss(lerp3(yellow, green, k));
}

/** Amber (low) -> orange (high). Gives the food bar its own visual identity. */
function foodColor(t: number): string {
  const amber: [number, number, number] = [0xb8, 0x6a, 0x1f];
  const orange: [number, number, number] = [0xf2, 0xa0, 0x3a];
  return rgbCss(lerp3(amber, orange, t));
}

function lerp3(
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): [number, number, number] {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
}

function rgbCss(c: [number, number, number]): string {
  return `rgb(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])})`;
}
